import { Select as RadixSelect } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface SelectOption {
	value: string;
	label: ReactNode;
	disabled?: boolean;
}

export interface SelectProps {
	options: readonly SelectOption[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	/** Подпись внутри поля, как у Input — форма читается одной вертикалью. */
	label?: string;
	/**
	 * Текст пустого поля. Обязателен: у кита нет текстовых умолчаний, а поле без
	 * значения и без подписи читается как сломанное.
	 */
	placeholder: string;
	size?: "md" | "sm";
	disabled?: boolean;
	required?: boolean;
	name?: string;
	hasError?: boolean;
	className?: string;
}

/**
 * Поле выбора: снаружи — Input, внутри — поповер.
 *
 * Заливка выровнена по Input (серый 100 без рамки), хотя в исходной DS у
 * Select была своя пара grey-150 + рамка grey-200 — в одной форме два разных
 * серых читаются как ошибка вёрстки, а не как иерархия.
 *
 * Выбранный пункт помечается галочкой, а не заливкой: зелёная заливка строки
 * меню в системе означала бы действие, а не состояние.
 */
export function Select({
	options,
	value,
	defaultValue,
	onValueChange,
	label,
	placeholder,
	size = "md",
	disabled,
	required,
	name,
	hasError,
	className,
}: SelectProps) {
	return (
		<RadixSelect.Root
			value={value}
			defaultValue={defaultValue}
			onValueChange={onValueChange}
			disabled={disabled}
			required={required}
			name={name}
		>
			<RadixSelect.Trigger
				aria-invalid={hasError || undefined}
				className={cn(
					"relative flex w-full items-center gap-2.5 bg-sunken text-left text-fg",
					"border border-transparent transition-[border-color,box-shadow] duration-150 ease-smooth",
					"data-[state=open]:border-brand data-[state=open]:shadow-[0_0_0_3px_var(--color-ring-halo)]",
					"focus-ring",
					"disabled:cursor-not-allowed disabled:opacity-60",
					size === "sm"
						? "rounded-field-sm px-2.5 py-1.5 t-body"
						: cn("rounded-card px-5 t-body", label ? "pt-[27px] pb-[17px]" : "py-[19px]"),
					hasError && "border-danger",
					className,
				)}
			>
				{label && size === "md" ? (
					<span className="absolute top-3.5 left-5 font-mono text-[10px] text-fg-muted uppercase leading-[1.2] tracking-[var(--ls-tight)]">
						{label}
					</span>
				) : null}
				<span className="min-w-0 flex-grow truncate">
					<RadixSelect.Value placeholder={<span className="text-fg-muted">{placeholder}</span>} />
				</span>
				<RadixSelect.Icon className="text-fg-muted">
					<Icon
						name="ChevronDown"
						size={20}
						className="transition-transform duration-[var(--dur-300)] ease-elegant data-[state=open]:rotate-180"
					/>
				</RadixSelect.Icon>
			</RadixSelect.Trigger>
			<RadixSelect.Portal>
				<RadixSelect.Content
					position="popper"
					sideOffset={2}
					// Зазор до края экрана: без него список ложится вплотную к
					// границе вьюпорта, а на узком экране вылезает за неё.
					collisionPadding={8}
					className={cn(
						// `z-130` — ВЫШЕ диалога и шторки (`z-120`). Иначе меню
						// открывается ПОД их затемнением: список не виден и не
						// кликается, а поле выглядит просто сломанным. Ровно так
						// нельзя было сменить роль пользователя в админке.
						// Потолок высоты — меньшее из 260px и того, что реально осталось
						// до края экрана: в коротком вьюпорте (телефон с открытой
						// клавиатурой) фиксированные 260px обрезаются молча.
						"iec-scroll z-130 max-h-[min(260px,var(--radix-select-content-available-height))]",
						"w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-content-available-width)]",
						"rounded-card bg-card p-1 shadow-float",
						"data-[state=open]:[animation:iec-reveal_var(--dur-400)_var(--ease-elegant)]",
					)}
				>
					<RadixSelect.Viewport className="flex flex-col gap-0.5">
						{options.map((option) => (
							<RadixSelect.Item
								key={option.value}
								value={option.value}
								disabled={option.disabled}
								className={cn(
									"iec-press-glyph iec-menu-item flex h-10 cursor-pointer items-center gap-2.5 rounded-control px-2.5 t-body text-fg",
									"outline-none transition-colors duration-150 ease-smooth",
									"data-[highlighted]:bg-sunken",
									"data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60",
								)}
							>
								<RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
								<RadixSelect.ItemIndicator className="ml-auto text-brand-text">
									<Icon name="Check" size={20} />
								</RadixSelect.ItemIndicator>
							</RadixSelect.Item>
						))}
					</RadixSelect.Viewport>
				</RadixSelect.Content>
			</RadixSelect.Portal>
		</RadixSelect.Root>
	);
}
